/**
 * What to tell somebody when a call does not start.
 *
 * The modal used to print the server's `detail` straight into a red line, so
 * "Phone call permission required" looked like a fault in the system rather
 * than a right the person has not been given. A failed call has three very
 * different causes and each needs a different sentence: you may not do this,
 * the provider is not set up, or something went wrong.
 *
 * Kept free of React so the mapping can be tested.
 */

export const CALL_ERROR_KINDS = {
  PERMISSION: 'permission',
  UNAVAILABLE: 'unavailable',
  NOT_FOUND: 'not_found',
  FAILED: 'failed',
};

const DETAIL_HINTS = [
  { kind: CALL_ERROR_KINDS.PERMISSION, match: /permission|berechtigung|not allowed|forbidden/i },
  { kind: CALL_ERROR_KINDS.UNAVAILABLE, match: /not configured|unavailable|nicht konfiguriert/i },
  { kind: CALL_ERROR_KINDS.NOT_FOUND, match: /not found|nicht gefunden/i },
];

/** The status wins; the text only helps when there is no status. */
export const callErrorKind = (error) => {
  const status = error?.response?.status || error?.status || null;
  if (status === 401 || status === 403) return CALL_ERROR_KINDS.PERMISSION;
  if (status === 503) return CALL_ERROR_KINDS.UNAVAILABLE;
  if (status === 404) return CALL_ERROR_KINDS.NOT_FOUND;
  const detail = String(error?.response?.data?.detail || error?.message || '');
  const hint = DETAIL_HINTS.find((entry) => entry.match.test(detail));
  return hint ? hint.kind : CALL_ERROR_KINDS.FAILED;
};

const MESSAGES = {
  [CALL_ERROR_KINDS.PERMISSION]: {
    title: 'You may not place calls',
    hint: 'Ask an administrator for the "Make Phone Calls" permission.',
  },
  [CALL_ERROR_KINDS.UNAVAILABLE]: {
    title: 'Calling is not available',
    hint: 'The telephone provider is not set up on this system.',
  },
  [CALL_ERROR_KINDS.NOT_FOUND]: {
    title: 'This conversation cannot be called',
    hint: 'It may have been closed, or it belongs to a protected customer.',
  },
  [CALL_ERROR_KINDS.FAILED]: {
    title: 'The call could not be started',
    hint: '',
  },
};

/**
 * A title somebody can act on, the reason underneath, and the server's own
 * words kept as the detail line — never thrown away, because that is what
 * support needs when the first two lines do not explain it.
 */
export const describeCallError = (error) => {
  const kind = callErrorKind(error);
  const detail = String(error?.response?.data?.detail || error?.message || '').trim();
  const message = MESSAGES[kind];
  return {
    kind,
    title: message.title,
    hint: message.hint,
    detail: detail && detail !== message.title ? detail : '',
  };
};
